import { useEffect, useState } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { RadiusCard } from 'Components/Cards/Radius/RadiusCard'
import { RadiusCardClickable } from 'Components/Cards/Radius/RadiusCardClickable'
import { type postError, type CareNetworkCommunication, AccountType, NoteSource, NoteType } from 'core/types'
import { FormatDateTimeValue, FormatDateValue, SpaceBeforeCapitals } from 'core/Utilities'
import { LabelWithToolTip } from 'Components/Label/Index'
import { Box, Button, IconButton } from '@mui/material'
import { AccessAlarms, DescriptionOutlined, ChatOutlined } from '@mui/icons-material'
import { CommunicationDetail } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationDetail'
import { toast } from 'react-toastify'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { usePermissions } from 'hooks/use-permissions'
import { useErrorDispatch } from 'ContextProviders/ErrorProvider'
import { useAuth } from 'hooks/use-auth'
import { OtherNoteDisplay } from 'Pages/CareNetworkManagement/CareNetworkDetail/OtherNoteDisplay'
import CardIcon from '@mui/icons-material/ArticleOutlined'
import TableIcon from '@mui/icons-material/TableRowsOutlined'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import { type PaginationProps, usePagination } from 'hooks/use-pagination'
import DataTable from 'Components/Table/DataTable'
import { CommunicationSearch, type CommunicationSearchCriteria } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationSearch'
import { CircleCheck } from 'Assets/SVGs/CircleCheck'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

export interface CommunicationProps {
  childId: number
}

export interface CommunicationSummary {
  id: number | null
  type: NoteSource
  enteredOn: Date
  note: string
  userName: string | null
  service: string | null
  serviceProvider: string | null
  timeFrame: string | null
  program: string | null
  isCompleted: boolean
}

export function CommunicationList (props: CommunicationProps): JSX.Element {
  const account = useAccountId()
  const auth = useAuth()
  const [refresh, setRefresh] = useState(new Date())
  const permissions = usePermissions()
  const dispatch = useErrorDispatch()
  const [communications, setCommunications] = useState<CommunicationSummary[]>([])
  const [selectedCommunication, setSelectedCommunication] = useState<CareNetworkCommunication | null>(null)
  const [selectedOtherNote, setSelectedOtherNote] = useState<CommunicationSummary | null>(null)
  const [users, setUsers] = useState<Array<{ id: number, name: string }>>([])
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)
  const [isNew, setIsNew] = useState<boolean>(false)
  const [isCardModeSelected, setIsCardModeSelected] = useState(true)
  const [searchData, setSearchData] = useState<CommunicationSearchCriteria>({ startDate: null, endDate: null, noteType: null })

  const storageKey = 'notesSortOrder'
  const storedSortOrder = window.localStorage.getItem(storageKey)
  const initialSortOrder = storedSortOrder !== null ? JSON.parse(storedSortOrder) : false
  const [cardDateSortAsc, setCardDateSortAsc] = useState<boolean>(initialSortOrder)

  useEffect(() => {
    const controller = new AbortController()
    const loadData = async (): Promise<void> => {
      let url = `/Communication/ChildCommunicationNotes?childid=${props.childId}`
      if (searchData.startDate !== null && searchData.startDate.toString() !== 'Invalid Date') {
        url += `&startDate=${searchData.startDate.toLocaleDateString()}`
      }
      if (searchData.endDate !== null && searchData.endDate.toString() !== 'Invalid Date') {
        url += `&endDate=${searchData.endDate.toLocaleDateString()}`
      }
      if (searchData.noteType !== null) {
        url += `&noteType=${searchData.noteType}`
      }
      const { response } = await sendGet(url, {})
      setCommunications(response)
    }
    void loadData()
    return () => { controller.abort() }
  }, [refresh])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      switch (account?.type) {
        case AccountType.ISD: {
          const { response } = await sendGet(`/ISDStaff/GetStaffList?isdID=${account?.id}`, {})
          if (response !== null) {
            setUsers(response)
          }
        }
          break
        case AccountType.SERVICE_PROVIDER: {
          const { response } = await sendGet(`/ServiceProviderSettings/GetProviderStaff?spID=${account?.id ?? 0}`, {})
          if (response !== null) {
            const users: Array<{ id: number, name: string }> = response.map((a: { id: number, firstName: string, lastName: string }) => {
              return { id: a.id, name: `${a.firstName} ${a.lastName}` }
            })
            setUsers(users)
          }
        }
          break
        case AccountType.INTAKE_USER: {
          const { response } = await sendGet(`/ServiceProviderSettings/GetIntakeUserStaff?spID=${account?.id ?? 0}`, {})
          if (response !== null) {
            const users: Array<{ id: number, name: string }> = response.map((a: { id: number, firstName: string, lastName: string }) => {
              return { id: a.id, name: `${a.firstName} ${a.lastName}` }
            })
            setUsers(users)
          }
        }
          break
      }
    }
    void loadData()
  }, [account])

  const markComplete = async (communicationID: number | null): Promise<void> => {
    if (communicationID !== null) {
      await sendPost(`/Communication/MarkReminderComplete?reminderID=${communicationID}`, {})
      setRefresh(new Date())
    }
  }

  const handleSearch = (newdata: CommunicationSearchCriteria): void => {
    setSearchData(newdata)
    if (newdata.startDate?.toString() !== 'Invalid Date' && newdata?.endDate?.toString() !== 'Invalid Date') {
      setRefresh(new Date())
    }
  }

  const handleSubmit = (com: CareNetworkCommunication): void => {
    const updateCommunication = async (): Promise<void> => {
      if (!isRequestLoading) {
        setIsRequestLoading(true)

        dispatch({ type: 'clear', errors: [] })

        let result: { response: any, error: string[], success: boolean } | null = null
        const isNew = com.id === 0
        const endpoint = `/Communication/${isNew ? 'Create' : 'Update'}Communication`
        result = await sendPost(endpoint, com)
        const rsp: { newRecordID: number, success: boolean, errors: postError[] } = result.response
        if (rsp.success) {
          setOpenModal(false)
          if (com.type === NoteType.Reminder) {
            const userindex = users.findIndex(x => x.id === com.userID)
            toast.success(`Communication record was updated. A reminder will be sent on ${FormatDateValue(com.reminderDate ?? new Date())} to ${users[userindex].name}`)
          } else {
            toast.success('Communication record was updated')
          }
          setRefresh(new Date())
        } else {
          dispatch({ type: 'update', errors: rsp.errors })
        }
        setIsRequestLoading(false)
      }
    }
    void updateCommunication()
  }
  const getContent = (communication: CommunicationSummary): JSX.Element => {
    return <Box>
      <LabelWithToolTip labelText={communication.note} />
      <Box sx={{ marginTop: 1, display: 'flex', flexDirection: 'row' }}>
        <Box>
          <LabelWithToolTip labelText={`${communication.type === NoteSource.Reminder ? 'Send Reminder to' : 'Logged by'} ${communication.userName ?? 'interest form'}`} />
        </Box>
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {communication.type === NoteSource.Reminder && !communication.isCompleted && <Button variant='contained' color='secondary' onClick={async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation()
            await markComplete(communication.id)
          }} data-testid='complete'
          >Complete</Button>}
          {communication.type === NoteSource.Reminder && communication.isCompleted &&
            <Box sx={{ marginTop: 1, display: 'flex', flexDirection: 'row' }}>
              <CircleCheck fill='#4DA4A4' />
              <Box sx={{ fontWeight: 'bold', pl: 0.5 }}>Reminder <br/>Completed</Box>
            </Box>
          }
        </Box>
      </Box>
    </Box>
  }

  const handleNewClick = (type: NoteType): void => {
    dispatch({ type: 'clear', errors: [] })
    const date = new Date(new Date().toLocaleString('en', { timeZone: 'America/New_York' }))

    const initialCommunicationState: CareNetworkCommunication = {
      userID: auth.user?.id ?? 0,
      addressedWhatMatteredMost: null,
      caregiverID: null,
      contactMethodID: null,
      enteredOn: date,
      id: 0,
      type,
      childID: props.childId,
      needMet: null,
      note: '',
      reasonNotMet: '',
      reminderDate: null,
      referralID: null,
      enrollmentID: null
    }

    setSelectedCommunication(initialCommunicationState)
    setIsNew(true)
    setOpenModal(true)
  }

  const handleClick = (communication: CommunicationSummary): void => {
    dispatch({ type: 'clear', errors: [] })
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(communication.type === NoteSource.AttendanceNote
        ? `/Communication/GetOtherNote?id=${String(communication.id)}&type=${communication.type}`
        : `/Communication/GetCommunication?id=${String(communication.id)}`, {})
      if (response !== null) {
        if (communication.type === NoteSource.AttendanceNote) {
          setSelectedCommunication(null)
          setSelectedOtherNote(response)
        } else {
          setSelectedCommunication(response)
          setSelectedOtherNote(null)
        }
        setIsNew(false)
        setOpenModal(true)
      }
    }
    void loadData()
  }

  const handleSortChange = (): void => {
    setCardDateSortAsc(!cardDateSortAsc)
    window.localStorage.setItem(storageKey, JSON.stringify(!cardDateSortAsc))
  }

  const typeIcon = (type: NoteSource): JSX.Element => {
    if (type === NoteSource.ContactLog) {
      return <ChatOutlined sx={{ width: 60, height: 60 }} />
    } else if (type === NoteSource.Reminder) {
      return <AccessAlarms sx={{ width: 60, height: 60 }} />
    } else {
      return <DescriptionOutlined sx={{ width: 60, height: 60 }} />
    }
  }

  const typeName = (type: NoteSource): string => {
    const text = type.toString().split(/(?=[A-Z])(?!^)/)
    if (text[0] !== '' && text[0] !== 'Note') { return text[0] + ' Note' }
    return text[0]
  }

  const sortedCommunications = [...communications].sort((a, b) => {
    const dateA = new Date(a.enteredOn).getTime()
    const dateB = new Date(b.enteredOn).getTime()
    return cardDateSortAsc ? dateA - dateB : dateB - dateA
  })

  const cardContent = <>
    <Button
      data-testid='sort-by-date-button'
      variant='text'
      color='secondary'
      className='mt-10'
      onClick={handleSortChange}
    >
      Sort by Date {cardDateSortAsc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
    </Button>
    {(permissions.CareNetwork_Communication && !permissions.CareNetwork_Communication_Edit) && communications != null &&
      <Box>
        {sortedCommunications.map((communication: CommunicationSummary, index) => (
          <Box sx={{ marginTop: 2 }} key={`${String(communication.type)}-${communication.id ?? index}`}>
            <RadiusCard
              icon={typeIcon(communication.type)}
              header={`${FormatDateValue(communication.enteredOn ?? new Date())} ${typeName(communication.type)}`}
              content={getContent(communication)}
              dataTestId={`communication-card-${communication.id ?? 0}`}
            />
          </Box>
        ))}
      </Box>
    }
    {(permissions.CareNetwork_Communication && permissions.CareNetwork_Communication_Edit) && communications != null &&
      <Box>
        {sortedCommunications.map((communication: CommunicationSummary, index) => (
          <Box sx={{ marginTop: 2 }} key={`${String(communication.type)}-${communication.id ?? index}`}>
            <RadiusCardClickable
              onClick={() => { handleClick(communication) }}
              icon={typeIcon(communication.type)}
              header={`${FormatDateValue(communication.enteredOn ?? new Date())} ${typeName(communication.type)}`}
              content={getContent(communication)}
              dataTestId={`communication-card-${communication.id ?? 0}`}
            />
          </Box>
        ))}
      </Box>
    }
  </>

  const paginationProps: PaginationProps<RowData> = {
    rows: communications
  }
  const pagination = usePagination(paginationProps)

  const typeColumn = new Column('type', 'Type', ColumnType.CUSTOM)
  typeColumn.customCellGenerator = row => {
    return <>{SpaceBeforeCapitals(row.type)}</>
  }

  const noteDateColumn = new Column('enteredOn', 'Entered On', ColumnType.CUSTOM)
  noteDateColumn.customCellGenerator = row => {
    return <>{FormatDateTimeValue(row.enteredOn)}</>
  }
  const noteColumn = new Column('note', 'Message')
  const columns = [typeColumn, noteDateColumn, noteColumn]

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    handleClick(row as CommunicationSummary)
  }

  const tableContent = <DataTable
    page={pagination.page}
    data-testid='session-blocks-table'
    name='sessionBlocksTable'
    hasCheckbox={false}
    columns={columns}
    rows={pagination.internalRows ?? []}
    totalRecords={pagination.recordCount}
    loading={false}
    hoverShading={false}
    rowClickable={permissions.CareNetwork_Communication_Edit}
    initialColumnSorts={[new ColumnSort('name')]}
    onSortChange={(col, sorts) => {
      pagination.onSortChange(col, sorts[0].order)
    }}
    onRowClick={rowClick}
    onPageChange={pagination.handleChangePage}
    onRowsPerPageChange={pagination.handleChangeRowsPerPage}
  />

  const handleModeChange = (isCardMode: boolean): void => {
    if (isCardMode) {
      setIsCardModeSelected(true)
    } else {
      setIsCardModeSelected(false)
    }
  }

  return (<>
    <CommunicationSearch current={searchData} onChange={handleSearch} />
    <Box className='d-flex f-direction-column'>
      <Box>
        {permissions.CareNetwork_Communication_Edit &&
          <>
            <Button variant='contained' color='secondary' className='mt-10' onClick={() => { handleNewClick(NoteType.ContactLog) }} data-testid='add-contact-log'>Add Contact Log</Button>
            <Button variant='contained' color='secondary' className='mt-10 ml-10' onClick={() => { handleNewClick(NoteType.Reminder) }} data-testid='add-reminder'>Add Reminder</Button>
            <Button variant='contained' color='secondary' className='mt-10 ml-10' onClick={() => { handleNewClick(NoteType.Note) }} data-testid='add-note'>Add Note</Button>
          </>
        }
        <IconButton onClick={() => { handleModeChange(true) }} className={'selectable-icon-button ' + (isCardModeSelected ? 'selected-icon' : '')}>
          <CardIcon className='selectable-icon' />
        </IconButton>
        <IconButton onClick={() => { handleModeChange(false) }} className={'selectable-icon-button ' + (!isCardModeSelected ? 'selected-icon' : '')}>
          <TableIcon className='selectable-icon' />
        </IconButton>
      </Box>
    </Box>

    {isCardModeSelected && cardContent}
    {!isCardModeSelected && tableContent}

    {openModal && selectedCommunication !== null && (
      <CommunicationDetail
        onClose={() => { setOpenModal(false) }}
        open={openModal}
        isNew={isNew}
        selectedCommunication={selectedCommunication}
        onSubmit={handleSubmit}
        userOptions={users}
      />
    )}
    {openModal && selectedOtherNote !== null && (
      <OtherNoteDisplay
        openModal={openModal}
        summary={selectedOtherNote}
        onClose={() => { setOpenModal(false) }}
      />
    )}
  </>)
}
